// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, h, Prop } from '@stencil/core'

import type { Size, Status } from '../shared'

export type BadgeIndicatorSize = Size | 'hero'
export type BadgeIndicatorStatus = Status | 'neutral'

/**
 * The `<qds-badge-indicator>` element is a small, rounded UI element used to
 * display status indicators. It is designed to draw the user's eye to
 * important or dynamic content by providing a visual cue that an event has
 * occurred or is waiting to be addressed.
 */
@Component({
  tag: 'qds-badge-indicator',
  shadow: true,
  styleUrl: 'badge-indicator.css',
})
export class BadgeIndicator implements ComponentInterface {
  /**
   * Adds accessible text to the qds-badge-indicator that will be used by
   * screen readers.
   *
   * @example
   * <qds-badge-indicator description="success"></qds-badge-indicator>
   * // This qds-badge-indicator will be read by screen readers as "success"
   */
  @Prop() public readonly description?: string

  /**
   * The badge indicator's size.
   */
  @Prop() public readonly size?: BadgeIndicatorSize = 'standard'

  /**
   * The badge indicator's status.
   */
  @Prop() public readonly status?: BadgeIndicatorStatus = 'neutral'

  /**
   * Shows a stroke ring around the badge indicator for better visibility.
   */
  @Prop() public readonly strokeRing: boolean = false

  get #computedStatus(): BadgeIndicatorStatus {
    switch (this.status) {
      case 'error':
      case 'info':
      case 'success':
      case 'warning': {
        return this.status
      }
      default: {
        return 'neutral'
      }
    }
  }

  get #computedSize(): BadgeIndicatorSize {
    switch (this.size) {
      case 'hero':
      case 'large':
      case 'small': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  public render() {
    return (
      <div
        class={{
          'qds-badge-indicator': true,
          'qds-stroke-ring': this.strokeRing,
        }}
        data-size={this.#computedSize}
        data-status={this.#computedStatus}
      >
        <span class="qds-sr-only">{this.description}</span>
      </div>
    )
  }
}
